import { Tooltip } from '../Tooltip/index.js'
import { MoreInfoCircle } from '../icons/index.js'

import styles from './InfoTooltip.module.scss'

/**
 * InfoTooltip — inline `(?)` icon that reveals a tooltip on hover / focus.
 *
 * Thin composition over `Tooltip` (handles positioning, edge-awareness,
 * animation, portal) and the `MoreInfoCircle` SVG (color-transitionable
 * via `currentColor`, so hover state animates smoothly).
 *
 * Use this whenever you want a dedicated hint marker next to a label.
 * For wrapping arbitrary content (icons, swatches, widgets) with a tooltip,
 * use `Tooltip` directly.
 *
 * @param {object}           props
 * @param {React.ReactNode}  props.text             — tooltip body (string or JSX)
 * @param {'top'|'bottom'}  [props.placement='top'] — preferred side; auto-flips if no room
 * @param {number|string}   [props.maxWidth]        — cap bubble width
 * @param {boolean}         [props.noWrap]          — single-line bubble
 * @param {boolean}         [props.disableOnMobile] — suppress on mobile (default false)
 */
export function InfoTooltip({ text, placement = 'top', maxWidth, noWrap, disableOnMobile }) {
  if (!text) return null

  return (
    <Tooltip
      title={text}
      placement={placement}
      maxWidth={maxWidth}
      noWrap={noWrap}
      disableOnMobile={disableOnMobile ?? false}
    >
      <span
        className={styles.icon}
        tabIndex={0}
        role="img"
        aria-label={typeof text === 'string' ? text : undefined}
      >
        <MoreInfoCircle />
      </span>
    </Tooltip>
  )
}
